import React, { createContext, useState, useContext, useEffect } from "react";
import customHttp from "../services/customHTTP"; // Import customHttp
import { useAuth } from "./authContext";
import { useLocation, useNavigate } from "react-router-dom";
// Create the context
const SiteContext = createContext();

// Custom hook to use the context
export const useSite = () => useContext(SiteContext);

// Create the provider component
export const SiteProvider = ({ children }) => {
	const [selectedSite, setSelectedSite] = useState(""); // Initially empty
	const [selectedSiteData, setSelectedSiteData] = useState([]); // Initially empty
	const [siteOptions, setSiteOptions] = useState([]); // State to hold the site options
	const [siteData, setSiteData] = useState([]); // State to hold the site options
	const [loading, setLoading] = useState(true); // State for loading
	const [counter, setCounter] = useState(0);
	const [selectedSiteCurrency, setSelectedSiteCurrency] = useState("");
	const [selectedSiteLegalEnitity, setSelectedSiteLegalEnitity] = useState("");
	const [selectedSiteWarehouse, setSelectedSiteWarehouse] = useState("");
	const [selectedSiteSName, setSelectedSiteSName] = useState("");
	const [currencySymbol, setCurrencySymbol] = useState("");
	const [loggedinUser, setLoggedinUser] = useState(() => {
		const sessionUser = sessionStorage.getItem("user"); // Store user data in sessionStorage
		return sessionUser ? JSON.parse(sessionUser) : null;
	});

	const navigate = useNavigate();
	const { user } = useAuth();

	const location = useLocation();
	const pathnames = location.pathname.split("/").filter((x) => x);

	const getCurrencySymbol = (selectedCurrency) => {
		const currencySymbols = {
			USD: "$", // US Dollar
			CAD: "CA$", // Canadian Dollar
			GBP: "£", // British Pound
			EUR: "€", // Euro
			SGD: "S$", // Singapore Dollar
			INR: "₹", // Indian Rupee
		};

		if (!selectedCurrency) {
			return currencySymbols["USD"];
		}

		const trimmedCurrency = selectedCurrency.trim().toUpperCase();
		const symbol = currencySymbols[trimmedCurrency];

		if (!symbol) {
			return currencySymbols["USD"];
		}

		return symbol;
	};

	useEffect(() => {
		if (loggedinUser) {
			fetchSites();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedinUser]);

	useEffect(() => {
		setLoggedinUser(user);
	}, [user]);

	const fetchSites = async () => {
		try {
			const requestBody = {
				query: `SELECT 
                    s.*,
                    sm.meta_value as site_legal_entity, 
                    sm1.meta_value as site_currency, 
                    sm2.meta_value as site_name, 
                    sm3.meta_value as warehouse 
                FROM sites as s
                LEFT JOIN 
                    site_meta as sm ON s.site_id=sm.site_id AND sm.meta_key='site_legal_entity'
                LEFT JOIN 
                    site_meta as sm1 ON s.site_id=sm1.site_id AND sm1.meta_key='site_currency'
                LEFT JOIN 
                    site_meta as sm2 ON s.site_id=sm2.site_id AND sm2.meta_key='site_name'
                LEFT JOIN 
                    site_meta as sm3 ON s.site_id=sm3.site_id AND sm3.meta_key='warehouse'
                WHERE s.site_id IN (${loggedinUser.user_sites})`,
			};

			const response = await customHttp.post("/cus_sites/getcustom", requestBody); // Send request with body
			const data = response.data.data;
			console.log("SitesData: ", data);
			// Filter distinct by 'customer_site_number' to avoid duplicates
			const distinctSites = data.filter(
				(value, index, self) =>
					index === self.findIndex((t) => t.customer_site_number === value.customer_site_number)
			);

			// Set options after filtering distinct sites
			const siteOptions = distinctSites.map((site) => ({
				key: site.site_id, // Assuming the site object has a 'site_id' field
				value: site.customer_site_number, // Assuming the site object has a 'site_id' field
				text: `${site.customer_site_name} - ${site.customer_site_number}`, // Display the site name
			}));

			setSiteOptions(siteOptions);
			setSiteData(data);
			setCounter(counter + 1);
			// Get the selected site from local storage or use the first one
			const storedSite = localStorage.getItem("selectedSite");
			if (storedSite && siteOptions.some((site) => site.value === storedSite)) {
				console.log("Stored site found:", storedSite);
				setSelectedSite(storedSite);
				const selectedSiteData = data.filter((site) => site.customer_site_number === storedSite)[0];
				setSelectedSiteData(selectedSiteData);
				setSelectedSiteLegalEnitity(selectedSiteData.site_legal_entity);
				setSelectedSiteWarehouse(selectedSiteData.warehouse);
				setSelectedSiteSName(selectedSiteData.site_name);
				const symbol = selectedSiteData?.site_currency
					? getCurrencySymbol(selectedSiteData.site_currency)
					: "$";
				const currency = selectedSiteData?.site_currency || "usd";
				setCurrencySymbol(symbol);
				setSelectedSiteCurrency(currency);
			} else if (siteOptions.length > 0) {
				console.log("No stored site found. Using the first site.");
				setSelectedSite(siteOptions[0].value); // Set the first site as default
				const selectedSiteData = data.filter((site) => site.customer_site_number === storedSite)[0];
				console.log("Selected Site Data: ", selectedSiteData);
				setSelectedSiteData(selectedSiteData);
				setSelectedSiteLegalEnitity(selectedSiteData?.site_legal_entity);
				setSelectedSiteWarehouse(selectedSiteData?.warehouse);
				setSelectedSiteSName(selectedSiteData?.site_name);
				const symbol = selectedSiteData?.site_currency
					? getCurrencySymbol(selectedSiteData?.site_currency)
					: "$";
				const currency = selectedSiteData?.site_currency || "usd";
				setCurrencySymbol(symbol);
				setSelectedSiteCurrency(currency);
				localStorage.setItem("selectedSite", siteOptions[0].value);
			}
		} catch (error) {
			console.error("Failed to fetch sites:", error);
		} finally {
			setLoading(false);
		}
	};

	const updateSite = (site, data) => {
		console.log(pathnames);
		setSelectedSite(site);
		const selectedSiteData = data.filter((s) => s.customer_site_number === site)[0];
		console.log(selectedSiteData.site_currency);
		setSelectedSiteData(selectedSiteData);

		setSelectedSiteLegalEnitity(selectedSiteData?.site_legal_entity);
		setSelectedSiteWarehouse(selectedSiteData?.warehouse);
		setSelectedSiteSName(selectedSiteData?.site_name);
		const symbol = selectedSiteData?.site_currency ? getCurrencySymbol(selectedSiteData.site_currency) : "$";
		const currency = selectedSiteData?.site_currency || "usd";
		setCurrencySymbol(symbol);
		setSelectedSiteCurrency(currency);
		localStorage.setItem("selectedSite", site); // Save the selected site to local storage
		if (
			pathnames.some((path) => !isNaN(path)) || // Check if any path segment is a number
			pathnames.includes("checkout") || // Check for "checkout"
			pathnames.includes("order-received") // Check for "order-received"
		) {
			console.log("Navigating to home...");
			navigate("/machines");
			return;
		}
	};

	return (
		<SiteContext.Provider
			value={{
				selectedSite,
				updateSite,
				siteOptions,
				loading,
				siteData,
				setLoggedinUser,
				selectedSiteData,
				selectedSiteCurrency,
				selectedSiteLegalEnitity,
				selectedSiteSName,
				selectedSiteWarehouse,
				currencySymbol,
				fetchSites,
			}}>
			{children}
		</SiteContext.Provider>
	);
};